import { useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { getNearbyHospitals } from '@/services/api';
import { MapPin } from 'lucide-react';
import { toast } from 'sonner';

export default function NearbyHospitalsPage() {
  const [postalCode, setPostalCode] = useState('');
  const [radius, setRadius] = useState(5000);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [origin, setOrigin] = useState(null);

  const searchHospitals = async (params) => {
    setLoading(true);
    try {
      const data = await getNearbyHospitals(params);
      setResults(data.hospitals || []);
      setOrigin(data.origin);
      toast.success(`Found ${data.count} hospitals`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to fetch hospitals');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchByPostal = async (event) => {
    event.preventDefault();
    if (!postalCode.trim()) {
      toast.error('Enter a postal code');
      return;
    }
    await searchHospitals({ postal_code: postalCode.trim(), radius });
  };

  const handleUseLocation = async () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        searchHospitals({ latitude, longitude, radius });
      },
      () => {
        setLoading(false);
        toast.error('Unable to access location');
      }
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="nearby-hospitals-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Nearby Hospitals
          </h1>
          <p className="text-slate-500">Find care centers using postal code or GPS.</p>
        </div>

        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-900" />Search Hospitals
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={handleSearchByPostal} className="grid md:grid-cols-3 gap-4">
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="postalCode">Postal Code</Label>
                <Input
                  id="postalCode"
                  value={postalCode}
                  onChange={(event) => setPostalCode(event.target.value)}
                  placeholder="Enter pincode or zip"
                  data-testid="hospital-postal-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="radius">Radius (meters)</Label>
                <Input
                  id="radius"
                  type="number"
                  min={1000}
                  max={20000}
                  value={radius}
                  onChange={(event) => setRadius(Number(event.target.value))}
                  data-testid="hospital-radius-input"
                />
              </div>
              <div className="md:col-span-3 flex flex-wrap gap-3">
                <Button
                  type="submit"
                  className="bg-blue-900 hover:bg-blue-800 text-white"
                  disabled={loading}
                  data-testid="hospital-search-btn"
                >
                  {loading ? 'Searching...' : 'Search by Postal Code'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleUseLocation}
                  disabled={loading}
                  data-testid="hospital-geolocation-btn"
                >
                  Use Current Location
                </Button>
              </div>
            </form>

            {origin && (
              <div className="text-sm text-slate-500" data-testid="hospital-origin">
                Search origin: {origin.latitude.toFixed(4)}, {origin.longitude.toFixed(4)}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-3" data-testid="hospital-results">
          {results.length === 0 && !loading ? (
            <p className="text-sm text-slate-500">No hospitals found yet. Run a search.</p>
          ) : (
            results.map((hospital) => (
              <Card key={hospital.id} className="bg-white border-slate-200 shadow-sm">
                <CardContent className="py-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900" data-testid={`hospital-name-${hospital.id}`}>{hospital.name}</p>
                      <p className="text-xs text-slate-500" data-testid={`hospital-address-${hospital.id}`}>{hospital.address}</p>
                    </div>
                    <Badge variant="secondary" data-testid={`hospital-distance-${hospital.id}`}>
                      {(hospital.distance_meters / 1000).toFixed(2)} km
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}